import React, { useState } from 'react';
import { getZoneColor, ZONE_ORDER } from '../utils/zoneUtils';

export interface OrbitDataPoint {
  subject: string;
  primary: number;
  secondary: number;
  fullMark: number;
}

interface OrbitChartProps {
  data: OrbitDataPoint[];
  primaryName: string;
  secondaryName: string;
  overallScore: number;
}

const MAX_RADIUS = 150;
const NUM_ZONES = ZONE_ORDER.length;
const CX = 175;
const CY = 175;

const ZONE_RINGS = [...ZONE_ORDER].map((zone, i) => {
  const fraction = (NUM_ZONES - i) / NUM_ZONES;
  return { zone, r: MAX_RADIUS * fraction };
});

const polarToXY = (angle: number, r: number) => ({
  x: CX + r * Math.cos(angle),
  y: CY + r * Math.sin(angle),
});

const rForScore = (score: number | null): number | null => {
  if (score == null || score < 0) return null;
  const clamped = Math.max(0, Math.min(100, score));
  return MAX_RADIUS * (clamped / 100);
};

export const ConsolidatedOrbitChart: React.FC<OrbitChartProps> = ({
  data,
  primaryName,
  secondaryName,
  overallScore
}) => {
  const [highlightIdx, setHighlightIdx] = useState<number | null>(null);

  if (!data.length) return null;

  const n = data.length;
  const angleStep = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2;

  const allPoints = data.map((d, i) => {
    const angle = startAngle + i * angleStep;
    const primaryR = rForScore(d.primary);
    const secondaryR = rForScore(d.secondary);
    return {
      data: d,
      angle,
      primaryR,
      secondaryR,
      primaryP: polarToXY(angle, primaryR ?? 0),
      secondaryP: polarToXY(angle, secondaryR ?? 0)
    };
  });

  const allSecondaryZero = data.every(d => d.secondary === 0);
  const allIdentical = !allSecondaryZero && data.every(d => d.primary === d.secondary);
  const isComparisonActive = secondaryName !== "No comparison selected";
  const noDataWarning = isComparisonActive && allSecondaryZero;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="flex items-center justify-center gap-8 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-[#1ecab8] shadow-[0_0_8px_rgba(30,202,184,0.5)] rounded" />
          <span className="text-[11px] font-bold text-slate-600 tracking-wide">{primaryName}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 border-t-2 border-dashed border-slate-400" />
          <span className={`text-[11px] font-bold tracking-wide ${!isComparisonActive ? 'text-slate-300' : 'text-slate-500'}`}>
            {secondaryName}
            {noDataWarning && <span className="ml-1 text-[10px] text-amber-500 font-black">(No data)</span>}
            {allIdentical && isComparisonActive && <span className="ml-1 text-[10px] text-blue-400 font-black">(Overlapping)</span>}
          </span>
        </div>
      </div>

      <div className="relative w-full max-w-[400px] aspect-square flex items-center justify-center">
        <svg viewBox={`0 0 ${CX * 2} ${CY * 2}`} width="100%" height="100%" style={{ overflow: 'visible' }}>
          <defs>
            <filter id="wb-shadow" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="2" stdDeviation="6" floodColor="#1ecab8" floodOpacity="0.2" />
            </filter>
          </defs>

          {[...ZONE_RINGS].map((ring, i) => {
            const zc = getZoneColor(ring.zone);
            const rOuter = ring.r;
            const rInner = i < ZONE_RINGS.length - 1 ? ZONE_RINGS[i+1].r : 0;
            const strokeW = rOuter - rInner;
            const rCenter = rInner + strokeW / 2;
            return (
              <g key={ring.zone}>
                <circle cx={CX} cy={CY} r={rCenter} fill="none" stroke={zc.color} strokeWidth={strokeW + 0.5} opacity={0.3} />
                <circle cx={CX} cy={CY} r={rOuter} fill="none" stroke="#ffffff" strokeWidth={1} />
              </g>
            );
          })}

          {allPoints.map((p, i) => {
            const outerP = polarToXY(p.angle, MAX_RADIUS);
            return (
              <g key={`axis-${i}`}>
                <line x1={CX} y1={CY} x2={outerP.x} y2={outerP.y} stroke="#ffffff" strokeWidth={1} strokeDasharray="4 3" opacity={0.8} />
                <text x={outerP.x + (outerP.x - CX) * 0.15} y={outerP.y + (outerP.y - CY) * 0.15} fill="#475569" fontSize={10} fontWeight={600} fontFamily="Inter, sans-serif" textAnchor="middle" dominantBaseline="middle">
                  {p.data.subject}
                </text>
              </g>
            );
          })}

          {noDataWarning && (
            <polygon
              points={allPoints.map(p => { const g = polarToXY(p.angle, MAX_RADIUS * 0.9); return `${g.x.toFixed(1)},${g.y.toFixed(1)}`; }).join(" ")}
              fill="none" stroke="rgba(156,163,175,0.25)" strokeWidth={1.5} strokeDasharray="5 4" strokeLinejoin="round"
            />
          )}

          {!allSecondaryZero && isComparisonActive && (
            <polygon
              points={allPoints.map(p => `${p.secondaryP.x.toFixed(1)},${p.secondaryP.y.toFixed(1)}`).join(" ")}
              fill="none"
              stroke={allIdentical ? "rgba(156,163,175,0.45)" : "rgba(156,163,175,0.9)"}
              strokeWidth={allIdentical ? 2.5 : 2}
              strokeDasharray="5 4" strokeLinejoin="round"
            />
          )}

          <polygon
            points={allPoints.map(p => `${p.primaryP.x.toFixed(1)},${p.primaryP.y.toFixed(1)}`).join(" ")}
            fill="none" stroke="#1ecab8" strokeWidth={2.5} strokeLinejoin="round" filter="url(#wb-shadow)"
          />

          {allPoints.map((p, i) => {
            const isHover = highlightIdx === i;
            return (
              <g key={`dot-${i}`} onMouseEnter={() => setHighlightIdx(i)} onMouseLeave={() => setHighlightIdx(null)} style={{ cursor: "pointer" }}>
                <circle cx={p.primaryP.x} cy={p.primaryP.y} r={15} fill="transparent" />
                {!allSecondaryZero && isComparisonActive && (
                  <circle cx={p.secondaryP.x} cy={p.secondaryP.y} r={3} fill={allIdentical ? "rgba(156,163,175,0.5)" : "#9ca3af"} />
                )}
                <circle cx={p.primaryP.x} cy={p.primaryP.y} r={isHover ? 6.5 : 4.5} fill="#ffffff" stroke="#1ecab8" strokeWidth={2} style={{ transition: "all 0.2s ease" }} />
                {isHover && (
                  <g>
                    <rect x={p.primaryP.x - 70} y={p.primaryP.y - 75} width={140} height={noDataWarning ? 45 : (isComparisonActive ? 60 : 45)} rx={8} fill="rgba(255,255,255,0.97)" stroke="#e2e8f0" style={{ filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.1))" }} />
                    <text x={p.primaryP.x} y={p.primaryP.y - 58} fontSize="9" fontWeight="bold" fill="#94a3b8" textAnchor="middle" letterSpacing="1">{p.data.subject.toUpperCase()}</text>
                    <text x={p.primaryP.x - 60} y={p.primaryP.y - 42} fontSize="10" fontWeight="bold" fill="#334155">{primaryName.length > 12 ? "Score:" : `${primaryName.substring(0,12)}:`}</text>
                    <text x={p.primaryP.x + 60} y={p.primaryP.y - 42} fontSize="11" fontWeight="900" fill="#1ecab8" textAnchor="end">{p.data.primary}</text>
                    {!allSecondaryZero && isComparisonActive && <>
                      <text x={p.primaryP.x - 60} y={p.primaryP.y - 25} fontSize="10" fontWeight="bold" fill="#64748b">Avg:</text>
                      <text x={p.primaryP.x + 60} y={p.primaryP.y - 25} fontSize="11" fontWeight="900" fill="#64748b" textAnchor="end">{p.data.secondary}</text>
                    </>}
                    {noDataWarning && <text x={p.primaryP.x} y={p.primaryP.y - 25} fontSize="9" fontWeight="bold" fill="#f59e0b" textAnchor="middle">No comparison data</text>}
                  </g>
                )}
              </g>
            );
          })}

          <circle cx={CX} cy={CY} r={28} fill="#ffffff" fillOpacity={0.9} stroke="#e2e8f0" strokeWidth={1} style={{ filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.05))' }} />
          <text x={CX} y={CY - 2} textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize={16} fontWeight={900}>{overallScore}</text>
          <text x={CX} y={CY + 12} textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize={7} fontWeight={800} letterSpacing={1}>OVERALL</text>

          {noDataWarning && (
            <g>
              <rect x={CX - 85} y={CY + 38} width={170} height={22} rx={11} fill="rgba(245,158,11,0.12)" stroke="rgba(245,158,11,0.4)" strokeWidth={1} />
              <text x={CX} y={CY + 49} textAnchor="middle" dominantBaseline="middle" fill="#b45309" fontSize={8} fontWeight={800} letterSpacing={0.5}>NO COMPARISON DATA FOR THIS FILTER</text>
            </g>
          )}

          {allIdentical && isComparisonActive && (
            <g>
              <rect x={CX - 80} y={CY + 38} width={160} height={22} rx={11} fill="rgba(59,130,246,0.1)" stroke="rgba(59,130,246,0.3)" strokeWidth={1} />
              <text x={CX} y={CY + 49} textAnchor="middle" dominantBaseline="middle" fill="#1d4ed8" fontSize={8} fontWeight={800} letterSpacing={0.5}>LINES OVERLAP — SAME DATASET</text>
            </g>
          )}
        </svg>
      </div>

      <div className="mt-8 flex flex-col items-center">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-4">Wellbeing Zones</p>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
          {ZONE_ORDER.map(zone => {
            const zc = getZoneColor(zone);
            return (
              <div key={zone} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: zc.color }} />
                <span className="text-[11px] font-semibold text-slate-500">{zone}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedOrbitChart;
